import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  MessageSquare,
  Eye,
  RefreshCw,
  Zap,
  CheckCircle2,
  Circle,
  AlertCircle,
  FileText,
  Send,
  Download,
  Filter,
  Search,
  BarChart3,
  ListTodo,
  Shield,
  FileCheck,
  ChevronRight,
  ChevronDown,
  Play,
  Pause,
  Bot,
  Scale
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [clients, setClients] = useState([]);
  const [taskAnalytics, setTaskAnalytics] = useState(null);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientTasks, setClientTasks] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [exportFilters, setExportFilters] = useState({
    categories: [],
    priorities: [],
    completed_only: false,
    pending_only: false
  });
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showLegalDialog, setShowLegalDialog] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, interviewsRes, clientsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/interviews`).catch(() => ({ data: { interviews: [] } })),
        axios.get(`${API}/admin/clients`).catch(() => ({ data: { clients: [] } })),
        axios.get(`${API}/admin/task-analytics`).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setInterviews(interviewsRes.data.interviews || []);
      setClients(clientsRes.data.clients || []);
      setTaskAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClientTasks = async (clientId) => {
    try {
      const response = await axios.get(`${API}/tasks/${clientId}`);
      setClientTasks(response.data);
    } catch (error) {
      console.error('Error fetching client tasks:', error);
      setClientTasks(null);
    }
  };

  const generateTasksForClient = async (clientId, businessInfo) => {
    try {
      const response = await axios.post(`${API}/tasks/generate`, {
        client_id: clientId,
        business_info: businessInfo
      });
      await fetchClientTasks(clientId);
      return response.data;
    } catch (error) {
      console.error('Error generating tasks:', error);
    }
  };

  const updateTaskStatus = async (clientId, taskId, status) => {
    try {
      await axios.put(`${API}/tasks/${clientId}/update`, {
        task_id: taskId,
        status: status,
        completed_by: 'admin'
      });
      await fetchClientTasks(clientId);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const exportTasksToClient = async (clientId) => {
    try {
      const response = await axios.post(`${API}/admin/client/${clientId}/send-tasks`, {
        client_id: clientId,
        ...exportFilters
      });
      setShowExportDialog(false);
      alert(`Exported ${response.data.exported_task_count} tasks for client delivery`);
    } catch (error) {
      console.error('Error exporting tasks:', error);
    }
  };

  const generateLegalDocs = async (clientId, businessInfo) => {
    try {
      const response = await axios.post(`${API}/legal/generate-documents`, {
        client_id: clientId,
        business_info: businessInfo
      });
      setShowLegalDialog(false);
      alert(`Generated ${response.data.document_count} legal documents`);
    } catch (error) {
      console.error('Error generating legal docs:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchClientTasks(selectedClient.client_id);
    }
  }, [selectedClient]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-700';
      case 'in_progress': return 'bg-amber-100 text-amber-700';
      case 'pending': return 'bg-slate-100 text-slate-700';
      case 'ready_for_payment': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  return (
    <div className="min-h-screen bg-blue-900">
      {/* Header */}
      <header className="bg-blue-950 border-b border-blue-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-blue-200 hover:text-white hover:bg-blue-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-blue-300 text-sm">Companeze BLDR Management Console</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchData} 
            disabled={isLoading}
            className="border-blue-600 text-blue-200 hover:bg-blue-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-blue-950 border border-blue-800 p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white text-blue-300">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="clients" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white text-blue-300">
              <Users className="w-4 h-4 mr-2" />
              Clients & Tasks
            </TabsTrigger>
            <TabsTrigger value="interviews" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white text-blue-300">
              <MessageSquare className="w-4 h-4 mr-2" />
              Interviews
            </TabsTrigger>
            <TabsTrigger value="legal" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white text-blue-300">
              <Scale className="w-4 h-4 mr-2" />
              Legal & Consent
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-blue-950 border-blue-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-300">Total Interviews</p>
                      <p className="text-3xl font-bold text-white">{stats?.total_interviews || 0}</p>
                    </div>
                    <div className="p-3 bg-blue-800 rounded-xl">
                      <Users className="w-6 h-6 text-blue-300" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-950 border-blue-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-300">Completed</p>
                      <p className="text-3xl font-bold text-emerald-400">{stats?.completed || 0}</p>
                    </div>
                    <div className="p-3 bg-emerald-900/50 rounded-xl">
                      <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-950 border-blue-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-300">Revenue</p>
                      <p className="text-3xl font-bold text-amber-400">${stats?.revenue || 0}</p>
                    </div>
                    <div className="p-3 bg-amber-900/50 rounded-xl">
                      <DollarSign className="w-6 h-6 text-amber-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-950 border-blue-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-300">Conversion</p>
                      <p className="text-3xl font-bold text-white">{stats?.conversion_rate || 0}%</p>
                    </div>
                    <div className="p-3 bg-purple-900/50 rounded-xl">
                      <TrendingUp className="w-6 h-6 text-purple-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card className="bg-blue-950 border-blue-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => navigate('/admin/publishing')}
                    className="bg-amber-500 hover:bg-amber-600 text-blue-900"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Document Publishing
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setActiveTab('interviews')}
                    className="border-blue-600 text-blue-200 hover:bg-blue-800"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    View Interviews
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setActiveTab('clients')}
                    className="border-blue-600 text-blue-200 hover:bg-blue-800"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Manage Clients
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Task Analytics */}
            {taskAnalytics && (
              <Card className="bg-blue-950 border-blue-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ListTodo className="w-5 h-5 text-amber-400" />
                    Task Analytics
                  </CardTitle>
                  <CardDescription className="text-blue-300">
                    Overall task completion across all clients
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-blue-300">Overall Completion</span>
                        <span className="text-white font-semibold">{taskAnalytics.completion_rate}%</span>
                      </div>
                      <Progress value={taskAnalytics.completion_rate} className="h-2" />
                      <p className="text-xs text-blue-400">
                        {taskAnalytics.completed_tasks} of {taskAnalytics.total_tasks} tasks completed
                      </p>
                    </div>
                    
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-blue-200">By Priority</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-red-400">High</span>
                          <span className="text-sm text-white">{taskAnalytics.tasks_by_priority?.high || 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-amber-400">Medium</span>
                          <span className="text-sm text-white">{taskAnalytics.tasks_by_priority?.medium || 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-blue-400">Low</span>
                          <span className="text-sm text-white">{taskAnalytics.tasks_by_priority?.low || 0}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-blue-200">AI Automation</h4>
                      <div className="flex items-center gap-3">
                        <Bot className="w-8 h-8 text-emerald-400" />
                        <div>
                          <p className="text-2xl font-bold text-white">{taskAnalytics.automation_candidates || 0}</p>
                          <p className="text-xs text-blue-400">Tasks can be automated</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Clients & Tasks Tab */}
          <TabsContent value="clients" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Client List */}
              <Card className="bg-blue-950 border-blue-800 lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-white">Clients</CardTitle>
                  <CardDescription className="text-blue-300">
                    Select a client to manage tasks
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {clients.length === 0 ? (
                    <p className="text-center py-8 text-blue-400">No clients with tasks yet</p>
                  ) : (
                    clients.map((client) => (
                      <div
                        key={client.client_id}
                        onClick={() => setSelectedClient(client)}
                        className={`p-4 rounded-lg cursor-pointer transition-colors ${
                          selectedClient?.client_id === client.client_id
                            ? 'bg-blue-700 border border-amber-500'
                            : 'bg-blue-900/50 hover:bg-blue-800 border border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-white">{client.client_id}</span>
                          <Badge className={`${client.completion_rate > 50 ? 'bg-emerald-600' : 'bg-amber-600'}`}>
                            {client.completion_rate}%
                          </Badge>
                        </div>
                        <Progress value={client.completion_rate} className="h-1" />
                        <p className="text-xs text-blue-400 mt-2">
                          {client.completed_tasks}/{client.total_tasks} tasks
                        </p>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Task Management */}
              <Card className="bg-blue-950 border-blue-800 lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white">
                        {selectedClient ? `Tasks: ${selectedClient.client_id}` : 'Select a Client'}
                      </CardTitle>
                      <CardDescription className="text-blue-300">
                        Manage and track client task completion
                      </CardDescription>
                    </div>
                    {selectedClient && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowExportDialog(true)}
                          className="border-blue-600 text-blue-200 hover:bg-blue-800"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Export to Client
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setShowLegalDialog(true)}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Generate Docs
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {!selectedClient ? (
                    <div className="text-center py-12 text-blue-400">
                      <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Select a client from the list to view their tasks</p>
                    </div>
                  ) : !clientTasks ? (
                    <div className="text-center py-12">
                      <p className="text-blue-400 mb-4">No tasks generated for this client</p>
                      <Button
                        onClick={() => generateTasksForClient(selectedClient.client_id, { name: selectedClient.client_id })}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        <Zap className="w-4 h-4 mr-2" />
                        Generate AI Tasks
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                      {clientTasks.tasks?.categories?.map((category, catIdx) => (
                        <div key={catIdx} className="border border-blue-800 rounded-lg overflow-hidden">
                          <div
                            onClick={() => toggleCategory(category.name)}
                            className="flex items-center justify-between p-4 bg-blue-900/50 cursor-pointer hover:bg-blue-800/50"
                          >
                            <div className="flex items-center gap-3">
                              {expandedCategories[category.name] ? (
                                <ChevronDown className="w-5 h-5 text-blue-400" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-blue-400" />
                              )}
                              <span className="font-semibold text-white">{category.name}</span>
                              <Badge variant="outline" className="border-blue-600 text-blue-300">
                                {category.tasks?.length || 0} tasks
                              </Badge>
                            </div>
                          </div>
                          
                          {expandedCategories[category.name] && (
                            <div className="p-4 space-y-3 bg-blue-950/50">
                              {category.tasks?.map((task, taskIdx) => (
                                <div
                                  key={taskIdx}
                                  className="flex items-start gap-3 p-3 bg-blue-900/30 rounded-lg border border-blue-800"
                                >
                                  <Checkbox
                                    checked={task.status === 'completed'}
                                    onCheckedChange={(checked) => 
                                      updateTaskStatus(
                                        selectedClient.client_id,
                                        task.id,
                                        checked ? 'completed' : 'pending'
                                      )
                                    }
                                    className="mt-1"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={`font-medium ${task.status === 'completed' ? 'text-blue-400 line-through' : 'text-white'}`}>
                                        {task.title}
                                      </span>
                                      <Badge className={getPriorityColor(task.priority)} variant="outline">
                                        {task.priority}
                                      </Badge>
                                      {task.automation_possible && (
                                        <Badge className="bg-emerald-900/50 text-emerald-400 border-emerald-700" variant="outline">
                                          <Bot className="w-3 h-3 mr-1" />
                                          Auto
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-sm text-blue-400 line-clamp-2">{task.description}</p>
                                    {task.estimated_hours && (
                                      <p className="text-xs text-blue-500 mt-1">
                                        <Clock className="w-3 h-3 inline mr-1" />
                                        {task.estimated_hours}h estimated
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Interviews Tab */}
          <TabsContent value="interviews">
            <Card className="bg-blue-950 border-blue-800">
              <CardHeader>
                <CardTitle className="text-white">Interview Sessions</CardTitle>
                <CardDescription className="text-blue-300">
                  View and manage AI interview sessions
                </CardDescription>
              </CardHeader>
              <CardContent>
                {interviews.length === 0 ? (
                  <div className="text-center py-12 text-blue-400">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No interviews yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {interviews.map((interview) => (
                      <div
                        key={interview.id}
                        className="flex items-center justify-between p-4 bg-blue-900/50 rounded-xl hover:bg-blue-800/50 transition-colors cursor-pointer border border-blue-800"
                        onClick={() => setSelectedInterview(interview)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center text-blue-900 font-bold">
                            {(interview.client_name || 'A')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-white">
                              {interview.client_name || 'Anonymous'}
                            </p>
                            <p className="text-sm text-blue-400">
                              {interview.client_email || 'No email'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-blue-300">
                              {interview.conversation?.length || 0} messages
                            </p>
                            <p className="text-xs text-blue-500">
                              {formatDate(interview.created_at)}
                            </p>
                          </div>
                          <Badge className={getStatusColor(interview.status)}>
                            {interview.status?.replace('_', ' ')}
                          </Badge>
                          <Button variant="ghost" size="icon" className="text-blue-300 hover:text-white">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Legal Tab */}
          <TabsContent value="legal" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-blue-950 border-blue-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-amber-400" />
                    Consent Agreements
                  </CardTitle>
                  <CardDescription className="text-blue-300">
                    Digital consent and indemnification tracking
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-900/50 rounded-lg border border-blue-800">
                      <h4 className="font-semibold text-white mb-2">Master Consent Agreement</h4>
                      <p className="text-sm text-blue-400 mb-3">
                        Includes hold harmless, indemnification, mediation requirement, and venue selection clauses.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-blue-600 text-blue-200 hover:bg-blue-800"
                        onClick={async () => {
                          const res = await axios.get(`${API}/legal/consent-agreement`);
                          console.log('Consent Agreement:', res.data);
                          alert('Consent agreement loaded. Check console for details.');
                        }}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Agreement
                      </Button>
                    </div>
                    
                    <div className="p-4 bg-amber-900/20 rounded-lg border border-amber-700/50">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5 text-amber-400" />
                        <h4 className="font-semibold text-amber-400">Legal Disclaimer</h4>
                      </div>
                      <p className="text-sm text-blue-300">
                        All legal documents are templates only. Companeze does not provide legal advice. 
                        Clients must have documents reviewed by qualified legal counsel.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-blue-950 border-blue-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-emerald-400" />
                    Document Templates
                  </CardTitle>
                  <CardDescription className="text-blue-300">
                    Generate customized legal document templates
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      'Operating Agreement (LLC)',
                      'Terms of Service',
                      'Privacy Policy',
                      'Service Agreement',
                      'Non-Disclosure Agreement',
                      'Independent Contractor Agreement'
                    ].map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-blue-900/50 rounded-lg border border-blue-800">
                        <span className="text-white text-sm">{doc}</span>
                        <FileText className="w-4 h-4 text-blue-400" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Interview Detail Modal */}
      <Dialog open={!!selectedInterview} onOpenChange={() => setSelectedInterview(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-blue-950 border-blue-800">
          <DialogHeader>
            <DialogTitle className="text-white">Interview Details</DialogTitle>
          </DialogHeader>
          {selectedInterview && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 p-4 bg-blue-900/50 rounded-xl">
                <div>
                  <p className="text-sm text-blue-400">Client</p>
                  <p className="font-semibold text-white">{selectedInterview.client_name || 'Anonymous'}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-400">Email</p>
                  <p className="font-semibold text-white">{selectedInterview.client_email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-400">Status</p>
                  <Badge className={getStatusColor(selectedInterview.status)}>
                    {selectedInterview.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-blue-400">Messages</p>
                  <p className="font-semibold text-white">{selectedInterview.conversation?.length || 0}</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3 text-white">Conversation</h4>
                <div className="space-y-3 max-h-96 overflow-y-auto p-4 bg-blue-900/30 rounded-xl">
                  {selectedInterview.conversation?.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-xl ${
                        msg.role === 'user' 
                          ? 'bg-amber-600 text-white ml-8' 
                          : 'bg-blue-800 text-blue-100 mr-8'
                      }`}
                    >
                      <p className="text-xs opacity-70 mb-1">{msg.role === 'user' ? 'Client' : 'Sarah (AI)'}</p>
                      <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Tasks Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="bg-blue-950 border-blue-800">
          <DialogHeader>
            <DialogTitle className="text-white">Export Tasks to Client</DialogTitle>
            <DialogDescription className="text-blue-300">
              Filter which tasks to export for client delivery
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="completed"
                checked={exportFilters.completed_only}
                onCheckedChange={(checked) => setExportFilters(prev => ({ ...prev, completed_only: checked, pending_only: false }))}
              />
              <label htmlFor="completed" className="text-sm text-blue-200">Completed tasks only</label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="pending"
                checked={exportFilters.pending_only}
                onCheckedChange={(checked) => setExportFilters(prev => ({ ...prev, pending_only: checked, completed_only: false }))}
              />
              <label htmlFor="pending" className="text-sm text-blue-200">Pending tasks only</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)} className="border-blue-600 text-blue-200">
              Cancel
            </Button>
            <Button onClick={() => exportTasksToClient(selectedClient?.client_id)} className="bg-amber-600 hover:bg-amber-700">
              <Send className="w-4 h-4 mr-2" />
              Export Tasks
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Legal Docs Dialog */}
      <Dialog open={showLegalDialog} onOpenChange={setShowLegalDialog}>
        <DialogContent className="bg-blue-950 border-blue-800">
          <DialogHeader>
            <DialogTitle className="text-white">Generate Legal Documents</DialogTitle>
            <DialogDescription className="text-blue-300">
              Generate customized legal document templates for this client
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-amber-900/20 rounded-lg border border-amber-700/50 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-amber-400" />
                <span className="font-semibold text-amber-400">Important Disclaimer</span>
              </div>
              <p className="text-sm text-blue-300">
                These documents are templates only and do not constitute legal advice. 
                Client must have all documents reviewed by qualified legal counsel before use.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLegalDialog(false)} className="border-blue-600 text-blue-200">
              Cancel
            </Button>
            <Button 
              onClick={() => generateLegalDocs(selectedClient?.client_id, { name: selectedClient?.client_id })} 
              className="bg-amber-600 hover:bg-amber-700"
            >
              <FileText className="w-4 h-4 mr-2" />
              Generate Documents
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
